import { useState } from 'react';
import PDFViewerModal from '../PDFViewerModal/PDFViewerModal';

interface Project {
  id: string;
  name: string;
  password: string;
  reportFile: string;
  description: string;
}

const projects: Project[] = [
  {
    id: 'usd-coinvertible',
    name: 'USD CoinVertible',
    password: 'USDC2024!',
    reportFile: '/audit-reports/usd-coinvertible/audit-report.pdf',
    description: 'USD-backed stablecoin audit report'
  },
  {
    id: 'e-pay-dollar',
    name: 'E-Pay Dollar',
    password: 'EPay$2024',
    reportFile: '/audit-reports/e-pay-dollar/document.pdf',
    description: 'E-Pay Dollar stablecoin attestation'
  },
  {
    id: 'quantoz-eurq',
    name: 'Quantoz EURQ',
    password: 'EURQ@Quant',
    reportFile: '/audit-reports/quantoz-eurq/audit-report.pdf',
    description: 'Euro-backed digital currency audit'
  },
  {
    id: 'plume-usd',
    name: 'Plume USD',
    password: 'Plume#USD1',
    reportFile: '/audit-reports/plume-usd/audit-report.pdf',
    description: 'Plume USD reserve verification'
  }
];

interface ReportDownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ReportDownloadModal = ({ isOpen, onClose }: ReportDownloadModalProps) => {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [showPDFViewer, setShowPDFViewer] = useState(false);

  if (!isOpen) return null;

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project);
    setPassword('');
    setError('');
  };

  const handleVerifyAndView = async () => {
    if (!selectedProject) return;

    setIsVerifying(true);
    setError('');

    // Simulate verification delay
    await new Promise(resolve => setTimeout(resolve, 800));

    if (password === selectedProject.password) {
      // Show PDF viewer instead of downloading
      setShowPDFViewer(true);
    } else {
      setError('Incorrect password. Please try again.');
    }

    setIsVerifying(false);
  };

  const handleBack = () => {
    setSelectedProject(null);
    setPassword('');
    setError('');
  };

  const handleClosePDFViewer = () => {
    setShowPDFViewer(false);
    setSelectedProject(null);
    setPassword('');
    setError('');
    // Also close the main modal when PDF viewer closes
    onClose();
  };

  const handleCloseMainModal = () => {
    // Reset all states when closing
    setSelectedProject(null);
    setPassword('');
    setError('');
    setShowPDFViewer(false);
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={handleCloseMainModal}
        />
        
        {/* Modal */}
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                {selectedProject ? 'Enter Password' : 'View Audit Reports'}
              </h2>
              <button
                onClick={handleCloseMainModal}
                className="text-white/80 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {!selectedProject ? (
              // Project List
              <div className="space-y-3">
                <p className="text-gray-600 text-sm mb-4">
                  Select a project to view its audit report. Each report is password protected and can only be viewed online.
                </p>
                {projects.map((project) => (
                  <button
                    key={project.id}
                    onClick={() => handleProjectSelect(project)}
                    className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all group text-left"
                  >
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                      <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">
                        {project.name}
                      </h3>
                      <p className="text-sm text-gray-500">{project.description}</p>
                    </div>
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ))}
              </div>
            ) : (
              // Password Entry
              <div className="space-y-4">
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to projects
                </button>

                <div className="bg-blue-50 rounded-xl p-4">
                  <h3 className="font-semibold text-blue-900">{selectedProject.name}</h3>
                  <p className="text-sm text-blue-700">{selectedProject.description}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter report password"
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                    onKeyDown={(e) => e.key === 'Enter' && handleVerifyAndView()}
                  />
                  {error && (
                    <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {error}
                    </p>
                  )}
                </div>

                <button
                  onClick={handleVerifyAndView}
                  disabled={!password || isVerifying}
                  className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {isVerifying ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Verifying...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View Report
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center">
                  This document can only be viewed online. Downloads are disabled for security.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-100">
            <p className="text-xs text-gray-500 text-center">
              All audit reports are confidential and password protected. 
              Contact support if you need access credentials.
            </p>
          </div>
        </div>
      </div>

      {/* PDF Viewer Modal */}
      {selectedProject && (
        <PDFViewerModal
          isOpen={showPDFViewer}
          onClose={handleClosePDFViewer}
          pdfUrl={selectedProject.reportFile}
          title={`${selectedProject.name} - Audit Report`}
        />
      )}
    </>
  );
};

export default ReportDownloadModal;
